import type { HTMLAttributes } from 'react';
import { cn } from '@skupreme/shared/ui';
import { useSidebar } from '@contexts/sidebar';
import { useSecureLayout } from '@hooks/useSecureLayout';

/*
 * The column the application sits in, right of the sidebar: a white card on
 * the grey frame. A fixed-height box, so the page scrolls inside it and the
 * bars within can stick to its top.
 *
 * `w-0` with `flex-auto` lets it take the row's remaining width without its
 * content (wide tables) pushing it past the window. The `2.5` insets are the
 * frame showing round it; the sidebar is flush against its left edge, so the
 * left inset only appears when a page hides the sidebar. A border, not the
 * hairline shadow, so rows painted to the edge cannot cover it.
 *
 * Login, onboarding and the other pages outside the app take the whole window
 * with no card, as they did before the frame existed.
 *
 * Positioned, so a page's `LoadingOverlay` (absolute, blurred) covers the card
 * only. Unpositioned, it spread over the sidebar too, and a page's first load
 * looked like the whole app reloading (Ely 10-22, Storage Providers).
 */
export function ContentCard({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  const isFramed = useSecureLayout();
  const { showSidebar } = useSidebar() as { showSidebar: boolean };

  return (
    <div
      {...rest}
      className={cn(
        'sk:relative sk:flex sk:w-0 sk:min-h-0 sk:flex-auto sk:flex-col sk:overflow-hidden',
        isFramed
          ? cn(
              'sk:my-2.5 sk:mr-2.5 sk:h-[calc(100vh_-_var(--sk-spacing)*5)]',
              'sk:rounded-xl sk:border sk:border-border-subtle sk:bg-surface sk:shadow-card',
              /* Below lg the sidebar is a drawer over the page, not a column. */
              !showSidebar ? 'sk:ml-2.5' : 'sk:max-lg:ml-2.5',
            )
          : 'sk:h-screen sk:bg-page',
        className,
      )}
    />
  );
}
